import { State } from "../state/state";
import Utils from "../../../utils";

/*
 *
 * facpOnOff | Latch
 * ------------------
 *     0     |   -    Inactive
 *     1     |   0    NonLatched
 *     1     |   1    Latched
 *
 * type
 * -----------------
 * 0                  Fail Safe
 * 1                  Fail Secure
 * 2                  Auxillary
 */

/**
 * @brief Convert Server side channel into UI friendly format.
 *
 * @param data:ChannelServer
 *
 * @return ChannelServer => Channel
 */
export const fromServer = (data: State.ChannelServer): State.Channel => {
  let facpCtrl: State.Inactive | State.Latching | State.NonLatching;
  if (data.facpOnOff) {
    if (data.latch) facpCtrl = "Latching";
    else facpCtrl = "NonLatching";
  } else {
    facpCtrl = "Inactive";
  }
  let ret: State.Channel = {
    ...data,
    ...{
      type: Utils.selectMap<State.OutputType>(data.type, [
        "Fail Safe",
        "Fail Secure",
        "Auxillary"
      ]),
      v: Utils.scaleUx(data.v, 1),
      i: Utils.scaleUx(data.i, 2),
      vHigh: Utils.scaleUx(data.vHigh, 1),
      vLow: Utils.scaleUx(data.vLow, 1),
      iHigh: Utils.scaleUx(data.iHigh, 2),
      iLow: Utils.scaleUx(data.iLow, 2),
      input_inv: Utils.numToBool(data.input_inv),
      noNc: Utils.numToBool(data.noNc),
      facp: Utils.numToBool(data.facp),
      facpOnOff: Utils.numToBool(data.facpOnOff),
      _facpCtrl: facpCtrl,
      latch: Utils.numToBool(data.latch),
      bBackup: Utils.numToBool(data.bBackup),
      eolFacp: Utils.numToBool(data.eolFacp),
      triggered: Utils.numToBool(data.triggered),
      locked: Utils.numToBool(data.locked),
      pcycl_dur: Utils.scaleUx(data.pcycl_dur)
    }
  };
  return ret;
};

/**
 * @brief Prepare some channel state changes to server format
 *
 * @param Partial<Channel>;
 *
 * @return Partial<Channel> => Partial<ChannelServer>;
 */
export const toServer = (
  data: Partial<State.Channel>
): Partial<State.ChannelServer> => {
  let ret: { latch?: 0 | 1; facpOnOff?: 0 | 1 } = {};
  if (data._facpCtrl) {
    if (data._facpCtrl == "Inactive") {
      ret.latch = 0;
      ret.facpOnOff = 0;
    } else if (data._facpCtrl == "Latching") {
      ret.latch = 1;
      ret.facpOnOff = 1;
    } else if (data._facpCtrl == "NonLatching") {
      ret.latch = 0;
      ret.facpOnOff = 1;
    }
  }
  let bools: Partial<State.ChannelServer> = Utils.convertBooleansToNumbers(
    data,
    ["input_inv", "noNc", "bBackup", "facp", "eolFacp", "triggered", "locked"]
  );
  let server: Partial<State.ChannelServer> = Utils.convertUxToServer(data, [
    "vHigh",
    "vLow",
    "iHigh",
    "iLow",
    "pcycl_dur",
    "i",
    "ical",
    "ioff",
    "v",
    "vcal",
    "voff"
  ]);
  if (data.hasOwnProperty("type") && !(typeof data.type == "undefined")) {
    server.type = Utils.selectMap<State.OutputType>(data.type, [
      "Fail Safe",
      "Fail Secure",
      "Auxillary"
    ]);
  }
  return Object.assign({}, data, bools, server, ret);
};
